import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

export type LessonBlockType =
  | 'text'
  | 'image'
  | 'rive'
  | 'mcq'
  | 'true_false'
  | 'flip_card_grid'
  | 'link_preview'
  | 'spacer';

export interface LessonBlock {
  id: string;
  type: LessonBlockType;
  position: number;
  title?: string;
  html?: string;
  payload: Record<string, any>;
  is_visible: boolean;
}

export interface LessonPage {
  id: string;
  title?: string;
  position: number;
  duration_hint?: number;
  cta_label?: string;
  is_intro?: boolean;
  is_summary?: boolean;
  blocks: LessonBlock[];
}

export interface LessonSnapshot {
  lesson_id?: string | null;
  lessonId?: string | null;
  title: string;
  status?: string;
  access_level?: 'free' | 'premium';
  curriculum_type?: 'ncert' | 'icse' | 'general' | 'misc';
  class_level?: number | null;
  chapter_title?: string | null;
  pages: LessonPage[];
}

export interface ValidationResult {
  errors: string[];
}

interface LessonState {
  lessonId: string | null;
  title: string;
  subjectId: number | null;
  subjectName: string;
  category: 'static' | 'current_affairs' | 'both';
  accessLevel: 'free' | 'premium';
  curriculumType: 'ncert' | 'icse' | 'general' | 'misc';
  classLevel: number | null;
  chapterTitle: string;
  difficulty: 'beginner' | 'medium' | 'advanced';
  labels: string[];
  applicableYears: number[];
  appliesUntilReplaced: boolean;
  pages: LessonPage[];
  selectedPageId: string | null;
  selectedBlockId: string | null;
  hasChanges: boolean;
  isSaving: boolean;
  isPublishing: boolean;
  error: string | null;

  // Page actions
  addPage: () => void;
  duplicatePage: (pageId: string) => void;
  deletePage: (pageId: string) => void;
  movePage: (fromIndex: number, toIndex: number) => void;
  selectPage: (pageId: string) => void;
  updatePage: (pageId: string, updates: Partial<LessonPage>) => void;

  // Block actions (page-scoped)
  addBlock: (type: LessonBlockType, pageId?: string) => void;
  updateBlock: (pageId: string, blockId: string, updates: Partial<LessonBlock>) => void;
  updatePayload: (pageId: string, blockId: string, payload: Record<string, any>) => void;
  moveBlock: (pageId: string, fromIndex: number, toIndex: number) => void;
  deleteBlock: (pageId: string, blockId: string) => void;
  duplicateBlock: (pageId: string, blockId: string) => void;
  selectBlock: (blockId: string | null) => void;

  // Lesson-level
  setTitle: (title: string) => void;
  setSubject: (subjectId: number | null, subjectName?: string) => void;
  setCategory: (category: 'static' | 'current_affairs' | 'both') => void;
  setAccessLevel: (accessLevel: 'free' | 'premium') => void;
  setCurriculumType: (curriculumType: 'ncert' | 'icse' | 'general' | 'misc') => void;
  setClassLevel: (classLevel: number | null) => void;
  setChapterTitle: (chapterTitle: string) => void;
  setDifficulty: (difficulty: 'beginner' | 'medium' | 'advanced') => void;
  setLabels: (labels: string[]) => void;
  setApplicableYears: (years: number[]) => void;
  setAppliesUntilReplaced: (value: boolean) => void;
  resetLesson: () => void;
  loadDraft: (data: Partial<LessonState>) => void;
  ensurePageExists: () => string;
  getSnapshot: () => LessonSnapshot;
  saveDraft: () => Promise<void>;
  publishLesson: () => Promise<void>;
}

const LOCAL_STORAGE_KEY = 'lesson-builder-draft';
const API_BASE =
  import.meta.env.PROD
    ? import.meta.env.VITE_LESSON_API_URL || 'https://api.clarityofconcept.com/api'
    : import.meta.env.VITE_LESSON_API_URL || 'http://localhost:8000/api';

// Lesson write endpoints are admin-gated on the server. Pull the admin JWT
// from localStorage (same key used by contexts/AuthContext.tsx + services/api.ts).
function authHeader(): Record<string, string> {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const defaultPayloads: Record<LessonBlockType, () => Record<string, any>> = {
  text: () => ({
    heading: 'New text block',
    body: 'Add your copy here...',
    align: 'left',
    variant: 'body',
    showHeading: true,
    useHtml: false,
    html: '<div style="text-align:left;"><h3>New text block</h3><p>Add your copy here...</p></div>',
  }),
  image: () => ({
    url: '',
    alt: '',
    caption: '',
    aspect: '16:9',
    fit: 'cover',
  }),
  rive: () => ({
    asset_url: '',
    artboard: '',
    stateMachine: '',
    autoplay: true,
    loop: 'auto',
    speed: 1,
    background: '#0b1220',
  }),
  mcq: () => ({
    question: 'Question text',
    question_html: '<p>Question text</p>',
    question_align: 'left',
    options: [
      { id: 'A', text: 'Option A', is_correct: true },
      { id: 'B', text: 'Option B', is_correct: false },
    ],
    explanation: '',
    shuffle: true,
    difficulty: 'medium',
  }),
  true_false: () => ({
    statement: 'Statement goes here',
    correct: true,
    explanation: '',
  }),
  flip_card_grid: () => ({
    columns: 2,
    cards: [
      { id: 'card-1', front: 'Front', back: 'Back', image: '' },
      { id: 'card-2', front: 'Front', back: 'Back', image: '' },
    ],
    max_cards: 12,
  }),
  link_preview: () => ({
    url: '',
    title: '',
    description: '',
    image: '',
  }),
  spacer: () => ({
    height: 24,
  }),
};

function makePage(position: number): LessonPage {
  return {
    id: `page-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    title: `Page ${position + 1}`,
    position,
    duration_hint: undefined,
    cta_label: position === 0 ? 'Start' : 'Next',
    is_intro: position === 0,
    is_summary: false,
    blocks: [],
  };
}

const normalizeCtas = (pages: LessonPage[]) => {
  pages.sort((a, b) => a.position - b.position);
  pages.forEach((p, i) => (p.position = i));
  const total = pages.length;
  pages.forEach((p, i) => {
    const label = (p.cta_label || '').trim();
    const norm = label.toLowerCase();
    const isStart = norm === 'start' || norm === 'begin';
    const isFinish = norm === 'finish' || norm === 'done';
    if (i === 0) {
      p.is_intro = true;
      p.is_summary = false;
      if (!isStart) p.cta_label = 'Start';
    } else if (i === total - 1) {
      p.is_intro = false;
      p.is_summary = true;
      if (!isFinish) p.cta_label = 'Finish';
    } else {
      p.is_intro = false;
      p.is_summary = false;
      if (!label || norm === 'start' || norm === 'finish' || norm === 'done') {
        p.cta_label = 'Next';
      }
    }
  });
};

export const useLessonBuilderStore = create<LessonState>()(
  immer((set, get) => ({
  lessonId: null,
  title: 'Untitled Lesson',
  subjectId: null,
  subjectName: '',
  category: 'static',
  accessLevel: 'free',
  curriculumType: 'general',
  classLevel: null,
  chapterTitle: '',
  difficulty: 'beginner',
  labels: [],
  applicableYears: [],
  appliesUntilReplaced: true,
  pages: [],
  selectedPageId: null,
  selectedBlockId: null,
  hasChanges: false,
  isSaving: false,
    isPublishing: false,
    error: null,

    ensurePageExists: () => {
      const state = get();
      if (state.pages.length > 0) {
        return state.selectedPageId || state.pages[0].id;
      }
      let newId = '';
      set((draft) => {
        const page = makePage(0);
        draft.pages.push(page);
        draft.selectedPageId = page.id;
        newId = page.id;
        draft.hasChanges = true;
      });
      return newId;
    },

    addPage: () => {
      set((state) => {
        const page = makePage(state.pages.length);
        state.pages.push(page);
        state.selectedPageId = page.id;
        state.selectedBlockId = null;
        state.hasChanges = true;
      });
    },

    duplicatePage: (pageId) => {
      set((state) => {
        const idx = state.pages.findIndex((p) => p.id === pageId);
        if (idx === -1) return;
        const source = state.pages[idx];
        const cloneBlocks = (source.blocks || []).map((b) => ({
          ...JSON.parse(JSON.stringify(b)),
          id: `block-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        }));
        const page: LessonPage = {
          ...JSON.parse(JSON.stringify(source)),
          id: `page-${Date.now()}-${Math.random().toString(16).slice(2)}`,
          position: source.position + 1,
          blocks: cloneBlocks,
        };
        state.pages.splice(idx + 1, 0, page);
        normalizeCtas(state.pages);
        state.selectedPageId = page.id;
        state.selectedBlockId = null;
        state.hasChanges = true;
      });
    },

   deletePage: (pageId) => {
     set((state) => {
       const idx = state.pages.findIndex((p) => p.id === pageId);
       if (idx === -1) return;
       state.pages.splice(idx, 1);
        if (state.pages.length === 0) {
          const page = makePage(0);
          state.pages.push(page);
          state.selectedPageId = page.id;
        } else {
          const nextIndex = Math.max(0, idx - 1);
          state.selectedPageId = state.pages[nextIndex].id;
        }
        state.selectedBlockId = null;
        state.hasChanges = true;
        normalizeCtas(state.pages);
     });
   },

   movePage: (fromIndex, toIndex) => {
     set((state) => {
       const [removed] = state.pages.splice(fromIndex, 1);
       state.pages.splice(toIndex, 0, removed);
       state.hasChanges = true;
        normalizeCtas(state.pages);
     });
   },

    selectPage: (pageId) => {
      set((state) => {
        const exists = state.pages.some((p) => p.id === pageId);
        if (exists) {
          state.selectedPageId = pageId;
          state.selectedBlockId = null;
        }
      });
    },

    updatePage: (pageId, updates) => {
      set((state) => {
        const page = state.pages.find((p) => p.id === pageId);
        if (page) {
          // If intro toggled off on first page, keep first page as intro
          if (updates.is_intro && state.pages.indexOf(page) !== 0) {
            updates.is_intro = false;
          }
          if (updates.is_summary && state.pages.indexOf(page) === 0) {
            updates.is_summary = false;
          }
          Object.assign(page, updates);
          normalizeCtas(state.pages);
          state.hasChanges = true;
        }
      });
    },

    addBlock: (type, pageId) => {
      const targetPageId = pageId || get().ensurePageExists();
      set((state) => {
        const page = state.pages.find((p) => p.id === targetPageId);
        if (!page) return;
        const basePayload = defaultPayloads[type]();
        const block: LessonBlock = {
          id: `block-${Date.now()}-${Math.random().toString(16).slice(2)}`,
          type,
          position: page.blocks.length,
          title: type === 'text' ? 'Text' : undefined,
          html: type === 'text' ? basePayload.html : undefined,
          payload: basePayload,
          is_visible: true,
        };
        page.blocks.push(block);
        state.selectedPageId = targetPageId;
        state.selectedBlockId = block.id;
        state.hasChanges = true;
      });
    },

    updateBlock: (pageId, blockId, updates) => {
      set((state) => {
        const page = state.pages.find((p) => p.id === pageId);
        if (!page) return;
        const block = page.blocks.find((b) => b.id === blockId);
        if (block) {
          Object.assign(block, updates);
          state.hasChanges = true;
        }
      });
    },

    updatePayload: (pageId, blockId, payload) => {
      set((state) => {
        const page = state.pages.find((p) => p.id === pageId);
        if (!page) return;
        const block = page.blocks.find((b) => b.id === blockId);
        if (block) {
          block.payload = { ...block.payload, ...payload };
          if (payload.html) {
            block.html = payload.html;
          }
          state.hasChanges = true;
        }
      });
    },

    moveBlock: (pageId, fromIndex, toIndex) => {
      set((state) => {
        const page = state.pages.find((p) => p.id === pageId);
        if (!page) return;
        const [removed] = page.blocks.splice(fromIndex, 1);
        page.blocks.splice(toIndex, 0, removed);
        page.blocks.forEach((b, idx) => {
          b.position = idx;
        });
        state.hasChanges = true;
      });
    },

    deleteBlock: (pageId, blockId) => {
      set((state) => {
        const page = state.pages.find((p) => p.id === pageId);
        if (!page) return;
        const idx = page.blocks.findIndex((b) => b.id === blockId);
        if (idx !== -1) {
          page.blocks.splice(idx, 1);
          page.blocks.forEach((b, index) => {
            b.position = index;
          });
          if (state.selectedBlockId === blockId) state.selectedBlockId = null;
          state.hasChanges = true;
        }
      });
    },

    duplicateBlock: (pageId, blockId) => {
      set((state) => {
        const page = state.pages.find((p) => p.id === pageId);
        if (!page) return;
        const block = page.blocks.find((b) => b.id === blockId);
        if (block) {
          const clone: LessonBlock = {
            ...JSON.parse(JSON.stringify(block)),
            id: `block-${Date.now()}-${Math.random().toString(16).slice(2)}`,
            position: block.position + 1,
          };
          page.blocks.splice(block.position + 1, 0, clone);
          page.blocks.forEach((b, index) => (b.position = index));
          state.selectedBlockId = clone.id;
          state.hasChanges = true;
        }
      });
    },

    selectBlock: (blockId) => set({ selectedBlockId: blockId }),

    setTitle: (title) => set({ title, hasChanges: true }),

    setSubject: (subjectId, subjectName) =>
      set({ subjectId, subjectName: subjectName || '', hasChanges: true }),

    setCategory: (category) => set({ category, hasChanges: true }),

    setAccessLevel: (accessLevel) => set({ accessLevel, hasChanges: true }),

    setCurriculumType: (curriculumType) =>
      set((state) => {
        state.curriculumType = curriculumType;
        if (curriculumType !== 'ncert' && curriculumType !== 'icse') {
          state.classLevel = null;
        }
        state.hasChanges = true;
      }),

    setClassLevel: (classLevel) => set({ classLevel, hasChanges: true }),

    setChapterTitle: (chapterTitle) => set({ chapterTitle, hasChanges: true }),

    setDifficulty: (difficulty) => set({ difficulty, hasChanges: true }),

    setLabels: (labels) => set({ labels, hasChanges: true }),

    setApplicableYears: (years) => set({ applicableYears: years, hasChanges: true }),

    setAppliesUntilReplaced: (value) => set({ appliesUntilReplaced: value, hasChanges: true }),

    resetLesson: () =>
      set(() => {
        const page = makePage(0);
        return {
          lessonId: null,
          title: 'Untitled Lesson',
          subjectId: null,
          subjectName: '',
          category: 'static',
          accessLevel: 'free',
          curriculumType: 'general',
          classLevel: null,
          chapterTitle: '',
          difficulty: 'beginner',
          labels: [],
          applicableYears: [],
          appliesUntilReplaced: true,
          pages: [page],
          selectedPageId: page.id,
          selectedBlockId: null,
          hasChanges: false,
          isSaving: false,
          isPublishing: false,
          error: null,
        };
      }),

    loadDraft: (data) => {
      set((state) => {
        if (data.title) state.title = data.title;
        if ((data as any).subjectId !== undefined) state.subjectId = (data as any).subjectId as any;
        if ((data as any).subject_id !== undefined) state.subjectId = (data as any).subject_id as any;
        if ((data as any).subjectName !== undefined) state.subjectName = (data as any).subjectName as any;
        if ((data as any).subject_name !== undefined) state.subjectName = (data as any).subject_name as any;
        if ((data as any).category) state.category = (data as any).category as any;
        if ((data as any).accessLevel !== undefined) state.accessLevel = (data as any).accessLevel as any;
        if ((data as any).access_level !== undefined) state.accessLevel = (data as any).access_level as any;
        if ((data as any).curriculumType !== undefined) state.curriculumType = (data as any).curriculumType as any;
        if ((data as any).curriculum_type !== undefined) state.curriculumType = (data as any).curriculum_type as any;
        if ((data as any).classLevel !== undefined) state.classLevel = (data as any).classLevel as any;
        if ((data as any).class_level !== undefined) state.classLevel = (data as any).class_level as any;
        if ((data as any).chapterTitle !== undefined) state.chapterTitle = (data as any).chapterTitle as any;
        if ((data as any).chapter_title !== undefined) state.chapterTitle = (data as any).chapter_title as any;
        if ((data as any).difficulty) state.difficulty = (data as any).difficulty as any;
        if ((data as any).labels) state.labels = (data as any).labels as any;
        if ((data as any).applicableYears) state.applicableYears = (data as any).applicableYears as any;
        if ((data as any).applicable_years) state.applicableYears = (data as any).applicable_years as any;
        if ((data as any).appliesUntilReplaced !== undefined) state.appliesUntilReplaced = Boolean((data as any).appliesUntilReplaced);
        if ((data as any).applies_until_replaced !== undefined) state.appliesUntilReplaced = Boolean((data as any).applies_until_replaced);
        if ((data as any).pages) state.pages = (data as any).pages as LessonPage[];
        if (data.lessonId) state.lessonId = data.lessonId;
        if ((data as any).lesson_id) state.lessonId = String((data as any).lesson_id);
        if (state.pages.length === 0) {
          const page = makePage(0);
          state.pages.push(page);
          state.selectedPageId = page.id;
        } else {
          state.pages.sort((a, b) => a.position - b.position);
          state.pages.forEach((p, i) => {
            p.position = i;
            if (i === 0) {
              p.is_intro = true;
              p.is_summary = false;
              if (!p.cta_label || p.cta_label === 'Next' || p.cta_label === 'Finish') {
                p.cta_label = 'Start';
              }
            } else if (i === state.pages.length - 1) {
              p.is_intro = false;
              p.is_summary = true;
              if (!p.cta_label || p.cta_label === 'Next' || p.cta_label === 'Start') {
                p.cta_label = 'Finish';
              }
            } else {
              p.is_intro = false;
              p.is_summary = false;
              if (!p.cta_label || p.cta_label === 'Start' || p.cta_label === 'Finish') {
                p.cta_label = 'Next';
              }
            }
          });
          state.selectedPageId = state.pages[0].id;
        }
        state.selectedBlockId = null;
        state.hasChanges = false;
      });
    },

    getSnapshot: () => {
      const pages = get()
        .pages
        .slice()
        .sort((a, b) => a.position - b.position)
        .map((p) => ({
          ...p,
          blocks: (p.blocks || [])
            .slice()
            .sort((a, b) => a.position - b.position)
            .map((b) => ({
              ...b,
              position: b.position,
            })),
        }));

      return {
        lesson_id: get().lessonId,
        lessonId: get().lessonId,
        title: get().title,
        subject_id: get().subjectId,
        subject_name: get().subjectName,
        category: get().category,
        access_level: get().accessLevel,
        curriculum_type: get().curriculumType,
        class_level: (get().curriculumType === 'ncert' || get().curriculumType === 'icse')
          ? get().classLevel
          : null,
        chapter_title: get().chapterTitle?.trim(),
        difficulty: get().difficulty,
        labels: get().labels,
        applicable_years: get().applicableYears,
        applies_until_replaced: get().appliesUntilReplaced,
        pages,
        status: 'draft',
      };
    },

    saveDraft: async () => {
      const snapshot = get().getSnapshot();
      set({ isSaving: true, error: null });

      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(snapshot));

        if (API_BASE) {
          const hasId = Boolean(get().lessonId);
          const endpoint = hasId
            ? `${API_BASE}/lessons/${get().lessonId}`
            : `${API_BASE}/lessons`;
          const method = hasId ? 'PUT' : 'POST';

          const res = await fetch(endpoint, {
            method,
            headers: { 'Content-Type': 'application/json', ...authHeader() },
            body: JSON.stringify(snapshot),
          });

          if (!res.ok) {
            const text = await res.text();
            throw new Error(text || `Save failed (${res.status})`);
          }

          // Try to capture lesson_id from response to keep linkage
          try {
            const data = await res.json();
            if (data?.lesson_id || data?.id) {
              set({ lessonId: data.lesson_id || data.id });
            }
          } catch {
            // Non-JSON is fine
          }
        }

        set({ hasChanges: false });
      } catch (error: any) {
        console.error('Lesson save failed', error);
        set({ error: error?.message || 'Save failed' });
        throw error;
      } finally {
        set({ isSaving: false });
      }
    },

    publishLesson: async () => {
      const { lessonId } = get();
      set({ isPublishing: true, error: null });

      try {
        // Ensure a save first (local + API)
        await get().saveDraft();

        if (API_BASE) {
          const id = get().lessonId || lessonId;
          const endpoint = id
            ? `${API_BASE}/lessons/${id}/publish`
            : `${API_BASE}/lessons/publish`;
          const res = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeader() },
          });

          if (!res.ok) {
            const text = await res.text();
            throw new Error(text || `Publish failed (${res.status})`);
          }
        }

        set({ hasChanges: false });
      } catch (error: any) {
        console.error('Lesson publish failed', error);
        set({ error: error?.message || 'Publish failed' });
        throw error;
      } finally {
        set({ isPublishing: false });
      }
    },
  }))
);

export function loadLessonDraftFromStorage() {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (error) {
    console.error('Failed to read lesson draft', error);
    return null;
  }
}

export function validateLessonSnapshot(snapshot: LessonSnapshot): ValidationResult {
  const errors: string[] = [];

  if (!snapshot.title || !snapshot.title.trim()) {
    errors.push('Lesson title is required.');
  }

  const hasSubject = Boolean((snapshot as any).subject_id) || Boolean((snapshot as any).subjectId) || Boolean((snapshot as any).subject_name);
  if (!hasSubject) {
    errors.push('Select a subject or enter a subject name.');
  }

  const curriculum = ((snapshot as any).curriculum_type || (snapshot as any).curriculumType || 'general').toString();
  const classLevelRaw = (snapshot as any).class_level ?? (snapshot as any).classLevel;
  const classLevel = classLevelRaw !== null && classLevelRaw !== undefined ? Number(classLevelRaw) : null;
  const chapterTitle = ((snapshot as any).chapter_title ?? (snapshot as any).chapterTitle ?? '').toString().trim();

  if (curriculum === 'ncert' || curriculum === 'icse') {
    if (classLevel === null || Number.isNaN(classLevel)) {
      errors.push('Class level is required for NCERT/ICSE lessons.');
    } else if (classLevel < 5 || classLevel > 12) {
      errors.push('Class level must be between 5 and 12 for NCERT/ICSE lessons.');
    }
  }
  if (!chapterTitle) {
    errors.push('Chapter title is required for all lessons.');
  }

  if (!snapshot.pages || snapshot.pages.length === 0) {
    errors.push('At least one page is required.');
    return { errors };
  }

  snapshot.pages.forEach((page, pageIndex) => {
    if (!page.blocks || page.blocks.length === 0) {
      errors.push(`Page ${pageIndex + 1}: add at least one block.`);
    }
    if (page.blocks && page.blocks.length > 12) {
      errors.push(`Page ${pageIndex + 1}: too many blocks (max 12).`);
    }

    page.blocks?.forEach((block, blockIndex) => {
      const label = `Page ${pageIndex + 1}, Block ${blockIndex + 1} (${block.type})`;
      if (block.type === 'text') {
        const useHtml = Boolean(block.payload?.useHtml);
        const htmlContent = typeof block.payload?.html === 'string' ? block.payload.html.trim() : '';
        const headingEnabled = block.payload?.showHeading !== false;
        const hasTextParts = Boolean((headingEnabled && block.payload?.heading?.trim()) || block.payload?.body?.trim());
        const hasContent = useHtml ? Boolean(htmlContent) || hasTextParts : hasTextParts;
        if (!hasContent) {
          errors.push(`${label}: text needs heading or body.`);
        }
      }
      if (block.type === 'mcq') {
        const options = block.payload?.options || [];
        if (options.length < 2) errors.push(`${label}: need at least 2 options.`);
        if (options.length > 6) errors.push(`${label}: too many options (max 6).`);
        const correctCount = options.filter((o: any) => o.is_correct).length;
        if (correctCount !== 1) errors.push(`${label}: exactly one correct option required.`);
        const htmlText = block.payload?.question_html
          ? String(block.payload.question_html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
          : '';
        const questionText = String(block.payload?.question || '').trim();
        if (!questionText && !htmlText) {
          errors.push(`${label}: question text is required.`);
        }
      }
      if (block.type === 'flip_card_grid') {
        const columns = block.payload?.columns || 2;
        if (columns < 1 || columns > 3) {
          errors.push(`${label}: columns must be 1-3.`);
        }
        const cards = (block.payload?.cards || []).filter((c: any) => c && (c.front || c.back || c.image));
        if (cards.length < 2) {
          errors.push(`${label}: add at least 2 cards.`);
        }
        if (cards.length > 12) {
          errors.push(`${label}: too many cards (max 12).`);
        }
        cards.forEach((c: any, i: number) => {
          const front = (c.front || '').trim();
          const back = (c.back || '').trim();
          if (!front && !back) {
            errors.push(`${label}: card ${i + 1} needs front or back text.`);
          }
        });
      }
      if (block.type === 'spacer') {
        const h = Number(block.payload?.height ?? 0);
        if (isNaN(h) || h < 4 || h > 320) {
          errors.push(`${label}: spacer height must be between 4 and 320px.`);
        }
      }
      if (block.type === 'true_false') {
        if (block.payload?.correct !== true && block.payload?.correct !== false) {
          errors.push(`${label}: select True or False.`);
        }
        if (!block.payload?.statement || !String(block.payload.statement).trim()) {
          errors.push(`${label}: statement is required.`);
        }
      }
      if (block.type === 'rive') {
        if (!block.payload?.asset_url || !String(block.payload.asset_url).trim()) {
          errors.push(`${label}: Rive asset URL is required.`);
        }
      }
    });
  });

  return { errors };
}
